/**
 * Form Management page walkthrough (IsfPageTour).
 * Targets must match data-tour attributes on ESourceTemplateList.jsx.
 *
 * @param {object} context
 * @param {boolean} [context.hasRows]
 * @param {boolean} [context.canDesign]
 * @param {boolean} [context.hasDraftRow]
 * @param {boolean} [context.hasPublishedOnlyRow]
 * @param {() => void} [context.openTourActionsMenu]
 * @param {() => void} [context.openTourDraftActionsMenu]
 * @param {() => void} [context.openTourPublishedOnlyActionsMenu]
 * @param {() => void} [context.closeTourActionsMenu]
 */
export function buildEsourceFormManagementTourSteps(context = {}) {
  const {
    hasRows = false,
    canDesign = true,
    hasDraftRow = false,
    hasPublishedOnlyRow = false,
    openTourActionsMenu,
    openTourDraftActionsMenu,
    openTourPublishedOnlyActionsMenu,
    closeTourActionsMenu,
  } = context;

  const openMenu = () => {
    try {
      openTourActionsMenu?.();
    } catch {
      /* tour */
    }
  };
  const openDraftMenu = () => {
    try {
      (openTourDraftActionsMenu || openTourActionsMenu)?.();
    } catch {
      /* tour */
    }
  };
  const openPublishedOnlyMenu = () => {
    try {
      (openTourPublishedOnlyActionsMenu || openTourActionsMenu)?.();
    } catch {
      /* tour */
    }
  };
  const closeMenu = () => {
    try {
      closeTourActionsMenu?.();
    } catch {
      /* tour */
    }
  };

  const steps = [
    {
      id: "welcome",
      placement: "center",
      title: "Welcome to Form Management",
      body: "e-Source Form Management lists every electronic source form for your organisation — drafts and published versions. Design in Form Builder, publish from this list, and use Audit log for history. Reopen anytime from Guide.",
    },
    {
      id: "guide-button",
      target: '[data-tour="esource-fm-guide"]',
      placement: "bottom",
      title: "Page guide",
      body: "Click Guide whenever you want this walkthrough again. Form Builder has its own Guide too.",
    },
    {
      id: "workflow",
      placement: "center",
      title: "How forms move from design to use",
      body: "1) Create Form opens Form Builder. 2) Build the canvas (Proto Types, AQ Assist chat or PDF/Word upload, or field types). 3) Save draft. 4) Publish from the row Actions menu on this page. 5) Published versions are read-only — create a new draft version to change them later.",
    },
    {
      id: "create",
      target: '[data-tour="esource-fm-create"]',
      placement: "bottom",
      title: "Create Form",
      body: "Starts a blank Form Builder. Proto Types and AQ Assist live inside the builder — not on this list.",
      when: () => canDesign,
    },
    {
      id: "refresh",
      target: '[data-tour="esource-fm-refresh"]',
      placement: "bottom",
      title: "Refresh",
      body: "Reload the latest forms, version numbers, and who last modified each row.",
    },
    {
      id: "table",
      target: '[data-tour="esource-fm-table"]',
      placement: "top",
      title: "Forms table",
      body: "Each row is one form. Columns show name/code, form type, published and draft versions, and created/modified details. Use column filters like other AQ report tables.",
    },
    {
      id: "preview",
      target: '[data-tour="esource-fm-preview"]',
      placement: "right",
      title: "Preview a form",
      body: "Click the form name to open a full-width preview (published version when available, otherwise the draft). Preview does not change the design.",
      when: () => hasRows,
      onLeave: closeMenu,
    },
    {
      id: "versions",
      target: '[data-tour="esource-fm-versions"]',
      placement: "bottom",
      title: "Draft vs published versions",
      body: "Published and Draft columns show version numbers (for example v1). Draft is what you edit. Publish makes the draft the live version for your organisation.",
      when: () => hasRows,
    },
    {
      id: "row-actions",
      target: '[data-tour="esource-fm-row-actions"]',
      placement: "left",
      title: "Row Actions (⋮)",
      body: "Open the Actions menu on a row for Edit draft, Create draft version, Publish, and Audit log. The next steps highlight each important action in the open menu.",
      when: () => canDesign && hasRows,
      onEnter: openMenu,
    },
    {
      id: "edit-draft",
      target: '[data-tour="esource-fm-action-edit"]',
      placement: "left",
      title: "Edit draft",
      body: "Opens Form Builder for the current draft so you can change fields, settings, and logic. Only appears when a draft version exists.",
      when: () => canDesign && hasDraftRow,
      onEnter: openDraftMenu,
    },
    {
      id: "create-draft",
      target: '[data-tour="esource-fm-action-create-draft"]',
      placement: "left",
      title: "Create draft version",
      body: "When a form is published and has no draft, use this to start a new draft from the live version. The published form stays unchanged until you publish again.",
      when: () => canDesign && hasPublishedOnlyRow,
      onEnter: openPublishedOnlyMenu,
    },
    {
      id: "publish",
      target: '[data-tour="esource-fm-action-publish"]',
      placement: "left",
      title: "Publish",
      body: "Releases the current draft as the live published version. You will confirm identity and give a reason. After publish, that version is read-only.",
      when: () => canDesign && hasDraftRow,
      onEnter: openDraftMenu,
    },
    {
      id: "audit",
      target: '[data-tour="esource-fm-action-audit"]',
      placement: "left",
      title: "Audit log",
      body: "Shows who created, saved drafts, published, or created draft versions for this form. You can export the log to Excel.",
      when: () => canDesign && hasRows,
      onEnter: openMenu,
      onLeave: closeMenu,
    },
    {
      id: "done",
      placement: "center",
      title: "You are ready",
      body: "Create or edit in Form Builder, preview from the form name, use ⋮ Actions to publish or open the audit log, then return here anytime. Use Guide for a refresher.",
      onEnter: closeMenu,
    },
  ];

  return steps.filter((step) => (step.when ? step.when(context) : true));
}
